import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { LutienApi } from '@src/services/lutien/lutien-api';

type TUserAction = {
  email: string;
  password: string;
};

export const cookieOptions = {
  expires: 7, // 7 days
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'strict' as const,
};

export const clearAuthCookies = () => {
  Cookies.remove('token');
  Cookies.remove('refreshToken');
  Cookies.remove('email');
  Cookies.remove('userId');
};

export const login = createAsyncThunk('authenticator/login', async (action: TUserAction) => {
  const lutienApi = new LutienApi();

  const loginResponse = await lutienApi.v1.auth.postLogin({
    body: {
      email: action.email,
      password: action.password,
    },
  });

  const userResponse = await lutienApi.v1.auth.getMe({ bearerToken: loginResponse.token });

  return {
    token: loginResponse.token,
    refreshToken: loginResponse.refreshToken,
    info: {
      email: action.email,
      id: userResponse.id,
    },
  };
});

const emptyState = {
  token: null,
  refreshToken: null,
  info: {
    email: null,
    id: null,
  },
};

export const authenticatorSlice = createSlice({
  name: 'authenticator',
  initialState: {
    user: (() => {
      const token = Cookies.get('token');
      const refreshToken = Cookies.get('refreshToken');
      const email = Cookies.get('email');
      const userId = Cookies.get('userId');

      return token
        ? {
            token,
            refreshToken: refreshToken || null,
            info: {
              email: email || null,
              id: userId || null,
            },
          }
        : emptyState;
    })(),
  },
  reducers: {
    logout: (state) => {
      state.user = emptyState;
      clearAuthCookies();
    },
    setUser: (
      state,
      action: { payload: { token: string; refreshToken: string | null; info: { email: string; id: string } } },
    ) => {
      state.user = action.payload;
      Cookies.set('token', action.payload.token, cookieOptions);
      if (action.payload.refreshToken) Cookies.set('refreshToken', action.payload.refreshToken, cookieOptions);
      Cookies.set('email', action.payload.info.email, cookieOptions);
      Cookies.set('userId', action.payload.info.id, cookieOptions);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      state.user = action.payload;
      Cookies.set('token', action.payload.token, cookieOptions);
      Cookies.set('refreshToken', action.payload.refreshToken, cookieOptions);
      Cookies.set('email', action.payload.info.email, cookieOptions);
      Cookies.set('userId', action.payload.info.id, cookieOptions);
    });
    builder.addCase(login.rejected, (state) => {
      state.user = emptyState;
      clearAuthCookies();
    });
  },
});

export const { logout, setUser } = authenticatorSlice.actions;
export const authenticatorReducer = authenticatorSlice.reducer;
